import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from '../MainPages.module.css';
import CardPlaceholder from '../../Components/UILibrary/CardPlaceholder';
import SearchBar from '../../Components/UILibrary/SearchBar';
import CategoryCard from '../../Components/Cart/CategoryCard';
import ConfirmModal from '../../Components/Modals/ConfirmModal';

import { CategoriesService } from '../../../Logic/CategoriesService';
import { useNavigate } from 'react-router-dom';

import type { ICategoryView } from '@model/Category.model';
import type { SearchItemType } from '@model/Search.model';

import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { useForceLogoutAndRedirect } from '../../../Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';

const ManageCategories: React.FC = () => {
  const navigate = useNavigate();

  const authStore = UserAuthenticationStoreImplementation();
  const onAuthError = useForceLogoutAndRedirect();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const categoriesService = new CategoriesService();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTitle, setDeleteTitle] = useState<string>('');
  const [meta, setMeta] = useState({ total: 0, page: 1, perPage: 10, pages: 0 });
  const requestParams = useMemo(
    () => ({ page: meta.page, perPage: meta.perPage }),
    [meta.page, meta.perPage]
  );

  const [categories, setCategories] = useState<ICategoryView[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = categoriesService.filterCategoriesBySearch(categories, searchQuery);
  useEffect(() => {
    const { page, perPage } = requestParams;
    categoriesService.handleGetAllAdminCategories({
      page,
      perPage,
      setLoading,
      setApiError: (message) => setApiError(message),
      token: authStore.authenticationState?.token ?? '',
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      setItems: (rows: ICategoryView[]) => setCategories(rows),

      setTotal: (t: number) =>
        setMeta((m) => ({
          ...m,
          total: t,
          page,
          perPage,
        })),
    });
  }, [requestParams.page, requestParams.perPage]);

  const handleConfirmDelete = useCallback(async () => {
    await categoriesService.handleDeleteCategory({
      id: deleteId,
      token: authStore.authenticationState?.token ?? '',
      setLoading,
      setApiError,
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      onSuccess: () => {
        setDeleteId(null);
        window.location.reload();
      },
    });
  }, [deleteId, authStore.authenticationState?.token]);

  return (
    <main className={styles.Page} data-testid="mc-page">
      <header className={styles.Header} data-testid="mc-header">
        <h1 className={styles.Title} data-testid="mc-title">
          Category Management
        </h1>

        <div className={styles.HeaderRight} data-testid="mc-header-right">
          <div data-testid="mc-searchbar">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search categories..."
              typesFilter={['category'] as SearchItemType[]}
              enableDropdown={false}
            />
          </div>

          <button
            className={styles.AddBtn}
            type="button"
            onClick={() => navigate('/admin/new-categories')}
            data-testid="mc-add-category"
          >
            Add Category
          </button>
        </div>
      </header>

      {/* <div className={styles.mcMetaRow} data-testid="mc-meta-row">
        <p className={styles.mcSubtitle} data-testid="mc-total">
          {typeof totalCount === "number"
            ? searchQuery
              ? `Found ${totalCount} matching categories`
              : `Total: ${totalCount}`
            : "Showing categories"}
        </p>

        <div className={styles.mcControls} data-testid="mc-controls">
          <div
            ref={perPageDropdownWrapRef}
            className={styles.mcPpdWrap}
            data-testid="mc-perpage-wrap"
          >
            <span className={styles.mcPpdLabel}>Per page</span>

            <button
              ref={perPageDropdownBtnRef}
              type="button"
              className={styles.mcPpdButton}
              aria-haspopup="listbox"
              aria-expanded={perPageDropdownOpen}
              aria-controls="perpage-menu"
              onClick={togglePerPageDropdown}
              onKeyDown={onPerPageDropdownButtonKeyDown}
              data-testid="mc-perpage-button"
            >
              {perPage}
              <span className={styles.mcPpdChevron} aria-hidden />
            </button>

            {perPageDropdownOpen && (
              <ul
                id="perpage-menu"
                ref={perPageDropdownListRef}
                role="listbox"
                tabIndex={-1}
                className={styles.mcPpdMenu}
                onKeyDown={onPerPageDropdownListKeyDown}
                aria-activedescendant={`ppd-opt-${perPageDropdownActiveIndex}`}
                data-testid="mc-perpage-menu"
              >
                {perPageOptions.map((option, index) => {
                  const isSelected = option === perPage;
                  const isActive = index === perPageDropdownActiveIndex;
                  return (
                    <li
                      id={`ppd-opt-${index}`}
                      key={option}
                      role="option"
                      aria-selected={isSelected}
                      className={[
                        styles.mcPpdOption,
                        isSelected ? styles.mcPpdSelected : "",
                        isActive ? styles.mcPpdActive : "",
                      ].join(" ")}
                      onMouseEnter={() => {}}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectPerPageAt(index)}
                      data-testid={`mc-perpage-option-${option}`}
                    >
                      {option}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {totalPages && (
            <div className={styles.mcPager} data-testid="mc-pager">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                data-testid="mc-pager-prev"
              >
                Prev
              </button>

              <span
                className={styles.mcPageIndicator}
                data-testid="mc-pager-indicator"
              >
                {page} / {totalPages}
              </span>

              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                data-testid="mc-pager-next"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div> */}

      {loading && (
        <div data-testid="mc-loading">
          <CardPlaceholder count={6} customWrapperStyle={styles.mcList} />
        </div>
      )}

      {!loading && apiError && (
        <div className={styles.Error} role="alert" data-testid="mc-error">
          {apiError}
        </div>
      )}

      {!loading && !apiError && filteredCategories.length === 0 && (
        <div className={styles.Empty} data-testid="mc-empty">
          No categories found{searchQuery ? ` for “${searchQuery}”` : ''}.
        </div>
      )}

      {!loading && !apiError && filteredCategories.length > 0 && (
        <section className={styles.mcList} data-testid="mc-list">
          {filteredCategories.map((category) => (
            <CategoryCard
              key={category.id}
              id={category.id}
              slug={category.slug ?? ''}
              productsCount={category.productsCount ?? 0}
              title={category.title}
              description={category.description}
              imageUrl={category.imageUrl ?? undefined}
              onEdit={() => navigate(`/admin/categories/${category.slug}/edit`)}
              onDelete={(id) => {
                setDeleteId(id);
                setDeleteTitle(category.title);
                setConfirmOpen(true);
              }}
            />
          ))}
        </section>
      )}

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleConfirmDelete}
        message={
          deleteTitle
            ? `Are you sure you want to delete the category "${deleteTitle}"?`
            : 'Are you sure you want to delete this category?'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </main>
  );
};

export default ManageCategories;
